import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DeliveryAddressService } from '@checkout-v2/feature';
import {
  SdModalService,
  SdvorIconComponent,
  SdvorInputComponent,
  SdvorToggleComponent,
  SystemMessageComponent,
} from '@sdvor/shared/ui/v2';
import { IconTypes } from '@shared/v2';
import { NgxMaskDirective } from 'ngx-mask';
import { DeliveryAddressDialogComponent } from '../../receipt-address/delivery/delivery-address-dialog';

@Component({
  selector: 'sd-lifting-options',
  imports: [
    ReactiveFormsModule,
    SdvorIconComponent,
    SdvorToggleComponent,
    SdvorInputComponent,
    NgxMaskDirective,
    SystemMessageComponent,
  ],
  templateUrl: './lifting-options.component.html',
  styleUrl: './lifting-options.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LiftingOptionsComponent implements OnInit {
  private readonly modalService = inject(SdModalService);
  private readonly deliveryAddressService = inject(DeliveryAddressService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly form = new FormGroup({
    enabled: new FormControl<boolean>(false),
    carryingDistance: new FormControl<number | string>('', { updateOn: 'blur' }),
  });

  // запрещаем вводить первым символом цифру 0
  protected readonly customPattern = {
    F: { pattern: new RegExp('[1-9]') },
    0: { pattern: new RegExp('[0-9]') },
  };

  protected readonly iconTypes = IconTypes;
  protected readonly deliveryAddress = toSignal(this.deliveryAddressService.activeAddress$);
  protected readonly hasFloor = computed(() => !!this.deliveryAddress()?.floor);
  protected readonly floorLiftInfo = computed(() => {
    const address = this.deliveryAddress();

    if (!address) {
      return '';
    }

    const floor = `${address.floor} этаж`;
    const lift = address.cargoLift ? 'грузовой лифт' : address.passengerLift ? 'пассажирский лифт' : 'без лифта';

    return `${floor}, ${lift}`;
  });

  constructor() {
    effect(() => {
      const address = this.deliveryAddress();

      if (!address) {
        return;
      }

      const carryingDistance = address.carryingDistance ?? 0;

      this.form.setValue(
        {
          enabled: carryingDistance > 30,
          carryingDistance: carryingDistance,
        },
        { emitEvent: false },
      );
    });
  }

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      const distance = value.enabled ? this.toNumber(value.carryingDistance) : 0;

      this.updateCarryingDistance(distance);
    });
  }

  openAddressModal(): void {
    const addressId = this.deliveryAddress()?.id;

    if (addressId) {
      this.deliveryAddressService.editableAddressId$.next(addressId);

      this.modalService.open(DeliveryAddressDialogComponent, {
        modalType: { xxs: 'fullscreen', lg: 'standard' },
        maxWidth: { lg: '1240px' },
      });
    }
  }

  toNumber(distance: unknown): number {
    const num = Number(distance) || 0;

    if (num <= 30) {
      return 0;
    }

    return num;
  }

  updateCarryingDistance(distance: number): void {
    const address = this.deliveryAddress();

    if (!address) {
      return;
    }

    const savedDistance = this.toNumber(address.carryingDistance);

    if (savedDistance === distance) {
      return;
    }

    this.deliveryAddressService.editSavedAddress({
      ...address,
      carryingDistance: distance,
    });
  }
}
